import React from "react";
import LegalPageLayout from "@/components/site/LegalPageLayout";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { LEGAL_ENTITY, COOKIES_TABLE, THIRD_PARTY_RESOURCES, CONSENT_CATEGORIES } from "@/data/legal";

const LAST_UPDATED = "11 de setembro de 2026";

const CookieRow = ({ cookie }) => (
  <tr>
    <td data-label="Nome">{cookie.name}</td>
    <td data-label="Tipo">{cookie.type}</td>
    <td data-label="Empresa">{cookie.provider}</td>
    <td data-label="Finalidade">{cookie.purpose}</td>
    <td data-label="Duração">{cookie.duration}</td>
  </tr>
);

export default function CookiePolicy() {
  const { openPreferences } = useCookieConsent();
  const necessaryCookies = COOKIES_TABLE.filter((c) => c.category === "necessary");
  const analyticsCookies = COOKIES_TABLE.filter((c) => c.category === "analyticsMarketing");

  return (
    <LegalPageLayout
      title="Política de Cookies"
      description={`Quais cookies o site da ${LEGAL_ENTITY.tradeName} usa e como gerenciar suas preferências.`}
      path="/politica-de-cookies"
      lastUpdated={LAST_UPDATED}
    >
      <div className="mn-legal-notice">
        <strong>Aviso:</strong> este é um modelo de referência, baseado em modelos comuns de
        mercado — não foi validado por um advogado. Passe por revisão jurídica antes de considerar
        esta página definitiva.
      </div>

      <h2>1. O que são cookies</h2>
      <p>
        Cookies são pequenos arquivos de texto que um site grava no seu navegador para lembrar
        informações entre visitas — como preferências ou dados de navegação. Alguns são
        essenciais para o funcionamento do site; outros ajudam a medir audiência ou personalizar
        anúncios.
      </p>

      <h2>2. Como gerenciar suas preferências</h2>
      <p>
        Você decide quais cookies aceita na primeira visita, através da nossa barra de
        consentimento. Pode mudar de ideia a qualquer momento clicando no botão abaixo (o mesmo
        link fica sempre disponível no rodapé do site, em "Preferências de cookies").
      </p>
      <button
        type="button"
        onClick={openPreferences}
        data-testid="cookie-policy-open-preferences"
        className="btn-mn-primary !py-3 !px-6 !text-[0.72rem] mb-8"
      >
        Gerenciar preferências de cookies
      </button>

      <h2>3. {CONSENT_CATEGORIES.necessary.label} (sempre ativos)</h2>
      <p>{CONSENT_CATEGORIES.necessary.description}</p>
      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Empresa</th>
              <th>Finalidade</th>
              <th>Duração</th>
            </tr>
          </thead>
          <tbody>
            {necessaryCookies.map((c) => (
              <CookieRow key={c.name} cookie={c} />
            ))}
          </tbody>
        </table>
      </div>

      <h2>4. {CONSENT_CATEGORIES.analyticsMarketing.label} (só com seu consentimento)</h2>
      <p>{CONSENT_CATEGORIES.analyticsMarketing.description}</p>
      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Empresa</th>
              <th>Finalidade</th>
              <th>Duração</th>
            </tr>
          </thead>
          <tbody>
            {analyticsCookies.map((c) => (
              <CookieRow key={c.name + c.provider} cookie={c} />
            ))}
          </tbody>
        </table>
      </div>

      <h2>5. Outros recursos de terceiros (não são cookies de rastreamento)</h2>
      <p>
        O site carrega alguns recursos de terceiros que não gravam cookies de rastreamento, mas
        fazem uma requisição direta aos servidores desses provedores:
      </p>
      <ul>
        {THIRD_PARTY_RESOURCES.map((r) => (
          <li key={r.name}>
            <strong>{r.name}</strong> ({r.domains}) — {r.note}
          </li>
        ))}
      </ul>

      <h2>6. Como desativar cookies pelo navegador</h2>
      <p>
        Além do nosso painel de preferências, você pode bloquear ou apagar cookies diretamente nas
        configurações do seu navegador:
      </p>
      <ul>
        <li>
          <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">
            Google Chrome
          </a>
        </li>
        <li>
          <a href="https://support.mozilla.org/pt-BR/kb/ative-e-desative-os-cookies-que-os-sites-usam" target="_blank" rel="noopener noreferrer">
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a href="https://support.apple.com/pt-br/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">
            Safari
          </a>
        </li>
        <li>
          <a href="https://support.microsoft.com/pt-br/microsoft-edge/excluir-cookies-no-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">
            Microsoft Edge
          </a>
        </li>
      </ul>
      <p>
        Bloquear cookies necessários pode afetar o funcionamento de algumas partes do site (como
        lembrar sua escolha de cookies).
      </p>
    </LegalPageLayout>
  );
}
